import { InfoLabelMessage } from "@msys/ui";
import { Stack } from "@mui/material";
import { GoogleMap, MarkerF } from "@react-google-maps/api";
import { useTranslate } from "@tolgee/react";
import { useField, useFormikContext } from "formik";
import { isString } from "lodash-es";
import React from "react";
import type { AddressInput as AddressInputGQLType } from "../../../clients/graphqlTypes.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import {
  getAddressInfoFromPlace,
  getLocationFromPlace,
  GoogleAddressSearch,
} from "../../commons/GoogleAddressSearch.js";
import { useGoogleMapsApi } from "../../google-maps/useGoogleMapsApi.js";
import { getAddressLabel } from "./helpers.js";

const defaultZoom = 15;
// Hackescher Markt
const defaultCenter = {
  lat: 52.523502,
  lng: 13.401785,
};

interface Props
  extends Pick<
    React.ComponentProps<typeof GoogleAddressSearch<AddressInputGQLType>>,
    "id" | "label" | "error" | "options" | "disabled" | "required" | "autoFocus"
  > {
  name: string;
  allowCustom?: boolean;
  showMap?: boolean;
  mapAspectRatio?: string;
}

export const AddressField = ({
  name,
  allowCustom = false,
  showMap = false,
  mapAspectRatio = "16/9",
  ...props
}: Props) => {
  const { isLoaded } = useGoogleMapsApi();
  const { t } = useTranslate("Address");
  const { isSubmitting } = useFormikContext<AddressInputGQLType>();
  const [{ value }, { error }, { setValue }] =
    useField<AddressInputGQLType | null>(name);

  const [center, setCenter] = React.useState<{ lat: number; lng: number }>(
    defaultCenter
  );
  const lastCenter = React.useRef<{ lat: number; lng: number }>(defaultCenter);

  React.useEffect(() => {
    if (value && value.location) {
      if (
        lastCenter.current.lat !== value.location.lat ||
        lastCenter.current.lng !== value.location.lng
      ) {
        const newValue = { lat: value.location.lat, lng: value.location.lng };
        lastCenter.current = newValue;
        setCenter(newValue);
      }
    }
  }, [value]);

  return (
    <Stack direction="column" spacing={1}>
      <GoogleAddressSearch
        {...props}
        initialValue={getAddressLabel(value)}
        onChange={value => {
          if (value === null) {
            setValue(null);
          } else if ("value" in value) {
            setValue(value.value);
          } else {
            const address = getAddressInfoFromPlace(value.place);
            const location = getLocationFromPlace(value.place);
            setValue({
              streetLines1: address.streetLines1,
              postalCode: address.postalCode ?? "",
              city: address.city ?? "",
              countryCode: address.countryCode ?? "",
              location,
            });
          }
        }}
        error={error && isString(error) ? error : undefined}
        disabled={props.disabled ?? isSubmitting}
      />
      {value && (
        <Stack direction="column" spacing={1}>
          <TextField
            name={`${name}.streetLines1`}
            label={t("Street and house number")}
            disabled={!allowCustom}
          />
          <Stack direction="row" spacing={1}>
            <TextField
              name={`${name}.postalCode`}
              label={t("Post code")}
              disabled={!allowCustom}
            />
            <TextField
              name={`${name}.city`}
              label={t("City/town")}
              disabled={!allowCustom}
            />
          </Stack>
          {isLoaded && (allowCustom || showMap) && (
            <>
              <GoogleMap
                options={{ mapTypeControl: false, streetViewControl: false }}
                mapTypeId={google.maps.MapTypeId.ROADMAP}
                mapContainerStyle={{
                  width: "100%",
                  aspectRatio: mapAspectRatio,
                }}
                zoom={defaultZoom}
                center={center}
              >
                <MarkerF
                  visible
                  draggable
                  position={
                    value && value.location
                      ? { lat: value.location.lat, lng: value.location.lng }
                      : defaultCenter
                  }
                  zIndex={1}
                  onDragEnd={async e => {
                    if (!e.latLng) return;
                    const newValue = {
                      lat: e.latLng.lat(),
                      lng: e.latLng.lng(),
                    };
                    lastCenter.current = newValue;
                    await setValue({
                      ...value,
                      location: newValue,
                    });
                  }}
                />
              </GoogleMap>
              {allowCustom && (
                <InfoLabelMessage
                  message={t("Drag marker to change address position on a map")}
                />
              )}
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};
